import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmployeeVisit } from '../employee-visit';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import {
  NGX_MAT_DATE_FORMATS,
  NgxMatDateAdapter,
  NgxMatDatetimePickerModule,
  NgxMatTimepickerModule,
} from '@angular-material-components/datetime-picker';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CustomDateAdapter, MY_DATE_FORMATS } from 'src/app/shared/customDateAdapter';
import { ComparingDatesEnum } from 'src/app/shared/enums/comparingDates.enum';
import { DateService } from 'src/app/shared/services/date.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { WorkOrdersRepository } from '../../workorders.repository';
import { EmployeeVisitDto } from 'src/api';
import { EmployeeVisitsView } from '../employee-visits-view-enum';
import { DrawerWorkorderActionsComponent } from 'src/workorders/drawers/drawer-workorder-actions/drawer-workorder-actions.component';
import { TranslateModule } from '@ngx-translate/core';
import { WorkOrderTranslateService } from 'src/app/shared/services/translate-workorder.service';
import { LocalizedDatePipe } from 'src/app/shared/localizedDatePipe';

@Component({
  selector: 'app-workorder-edit-employee-visits-wizard',
  standalone: true,
  providers: [
    { provide: NGX_MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: NgxMatDateAdapter, useClass: CustomDateAdapter },
  ],
  imports: [
    CommonModule,
    MatButtonModule,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatAutocompleteModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    DrawerWorkorderActionsComponent,
    TranslateModule,
    LocalizedDatePipe,
  ],
  templateUrl: './edit-employee-visits-wizard.component.html',
  styleUrls: ['./edit-employee-visits-wizard.component.scss'],
})
export class EditEmployeeVisitsWizardComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;
  @Input({ required: true }) workOrderId!: number;
  @Input({ required: true }) employeeVisits!: EmployeeVisit[];
  @Output() employeeVisitsViewChange = new EventEmitter<EmployeeVisitsView>();

  employeeVisitsToEdit!: EmployeeVisit[];
  visitEndsNextDay = false;

  maxStartDate: Date = new Date();
  maxEndDate: Date = new Date();
  startDateControl = new FormControl(new Date());
  endDateControl = new FormControl(new Date());

  constructor(
    private readonly workOrderRepository: WorkOrdersRepository,
    private readonly dialog: MatDialog,
    private readonly workorderTranslateService: WorkOrderTranslateService
  ) {}

  ngOnInit() {
    this.loadEmployeeVisitsToEdit();
  }

  loadEmployeeVisitsToEdit() {
    if (this.employeeVisits.length > 0) {
      this.employeeVisitsToEdit = this.employeeVisits
        .map((ev) => {
          return {
            id: ev.id,
            startDate: ev.startDate,
            endDate: ev.endDate,
            employeeId: ev.employeeId,
            employeeFullName: ev.employeeFullName,
            employeeAbbreviatedName: ev.employeeAbbreviatedName,
            removeVisit: true,
          } as EmployeeVisit; //create new objects to use in this component
        })
        .sort(
          (a, b) =>
            a.employeeAbbreviatedName!.toLowerCase().localeCompare(b.employeeAbbreviatedName!.toLowerCase()) ||
            a.startDate.getTime() - b.startDate.getTime()
        );

      const startDate = new Date(this.employeeVisitsToEdit[0].startDate!);
      const endDate = new Date(this.employeeVisitsToEdit[0].endDate!);
      this.startDateControl.setValue(startDate);
      this.endDateControl.setValue(endDate);
      this.maxStartDate = DateService.createNewDate(startDate);
      this.maxEndDate = DateService.createDateWithAddedDays(startDate, 1);
    }
  }

  changeCalendarDate() {
    const isEndDateBiggerThanMaxDate = DateService.compareDatesWhitoutTime(
      this.endDateControl.value!,
      DateService.createDateWithAddedDays(this.startDateControl.value!, 1),
      ComparingDatesEnum.Bigger
    );
    if (isEndDateBiggerThanMaxDate || this.startDateControl.value! > this.endDateControl.value!) {
      this.endDateControl.setValue(this.startDateControl.value!);
    }

    this.visitEndsNextDay = DateService.compareDatesWhitoutTime(
      this.endDateControl.value!,
      this.startDateControl.value!,
      ComparingDatesEnum.Bigger
    );

    if (this.employeeVisitsToEdit.length > 0) {
      this.employeeVisitsToEdit.map((e) => {
        this.updateEmployeeVisitDates(e, this.startDateControl.value!, this.endDateControl.value!);
      });
    }
  }

  changeEmployeeStartTime(startTime: string, employeeVisit: EmployeeVisit) {
    const splitTime = startTime.split(':');
    let startDate = DateService.createNewDateWithTime(
      employeeVisit.startDate,
      parseInt(splitTime[0]),
      parseInt(splitTime[1])
    );

    let endDate = employeeVisit.endDate;
    if (startDate > endDate) {
      endDate = startDate;
    }

    this.updateEmployeeVisitDates(employeeVisit, startDate, endDate);
  }

  changeEmployeeEndTime(endTime: string, employeeVisit: EmployeeVisit) {
    const splitTime = endTime.split(':');
    let startDate = employeeVisit.startDate;
    let endDate = DateService.createNewDateWithTime(
      employeeVisit.endDate,
      parseInt(splitTime[0]),
      parseInt(splitTime[1])
    );

    if (startDate > endDate) {
      startDate = endDate;
    }

    this.updateEmployeeVisitDates(employeeVisit, startDate, endDate);
  }

  removeEmployeeVisit(employeeVisit: EmployeeVisit) {
    if (employeeVisit) {
      employeeVisit.removeVisit = false;
    }
  }

  updateEmployeeVisitDates(employeeVisit: EmployeeVisit, startDate: Date, endDate: Date) {
    employeeVisit.startDate = startDate;
    employeeVisit.endDate = endDate;
  }

  saveEmployeeVisits() {
    if (this.employeeVisitsToEdit && this.employeeVisitsToEdit.length > 0) {
      let employeeVisitsToRemove = this.employeeVisitsToEdit.filter((ev) => !ev.removeVisit);

      if (employeeVisitsToRemove.length > 0) {
        const removeMsg = this.workorderTranslateService.translateRemoveVisitsMessage();
        const andChar = this.workorderTranslateService.translateAndCharacter();

        const removedEmployees = [...new Set(employeeVisitsToRemove.map((ev) => ev.employeeAbbreviatedName))];
        const displayEmployees = [
          ...removedEmployees.slice(0, -2),
          removedEmployees.slice(-2).join(andChar + ' '),
        ].join(', ');

        let dialogRef = this.dialog.open(ConfirmDialogComponent, {
          width: 'auto',
          data: {
            confirm: false,
            msg: removeMsg,
            displayInfo: displayEmployees,
          },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            const employeeIdsToRemove = employeeVisitsToRemove.map((ev) => ev.id as number);
            this.updateEmployeeVisits(employeeIdsToRemove);
          } else {
            this.employeeVisitsToEdit.map((ev) => (ev.removeVisit = true));
          }
        });
      } else {
        this.updateEmployeeVisits([]);
      }
    }
  }

  updateEmployeeVisits(employeeVisitsToRemove: number[]) {
    let employeeVisitsToUpdate = this.employeeVisitsToEdit.map((e) => {
      return {
        id: e.id,
        workOrderId: this.workOrderId,
        employeeId: e.employeeId,
        startDate: DateService.convertDateToString(e.startDate),
        endDate: DateService.convertDateToString(e.endDate),
      } as EmployeeVisitDto;
    });

    this.workOrderRepository
      .updateRemoveWorkOrderEmployeeVisits(this.workOrderId, employeeVisitsToUpdate, employeeVisitsToRemove)
      .then(() => {
        this.employeeVisitsViewChange.emit(EmployeeVisitsView.List);
      });
  }
}
